/**
 * Every dialog on the stocktake surface. They are deliberately sequential,
 * never stacked (DESIGN.md §10): the approve confirm closes before the
 * decision-summary composer opens, so focus is never trapped two layers deep.
 */
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Download, FileSpreadsheet, Loader2, TriangleAlert, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  StocktakeDriftedLine,
  StocktakeNotifyConfig,
  StocktakeNotifyMode,
} from '@threadheads/shared';
import { StocktakeComposer } from './StocktakeComposer';
import { formatQty, formatSigned } from '../lib/format';

// ---------------------------------------------------------------------------
// Approve one count
// ---------------------------------------------------------------------------

export function ApproveCountDialog({
  open,
  onOpenChange,
  locationName,
  counterpartyName,
  acceptCount,
  recountCount,
  rejectCount,
  netChange,
  onContinue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationName: string;
  counterpartyName: string;
  acceptCount: number;
  recountCount: number;
  rejectCount: number;
  netChange: number;
  onContinue: () => void;
}) {
  const { t } = useTranslation('inventory');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('stocktakes.dialog.approve.title', {
              count: acceptCount,
              lines: acceptCount.toLocaleString(),
              location: locationName,
            })}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2.5 text-left">
              <p>
                <Trans
                  i18nKey="stocktakes.dialog.approve.body"
                  ns="inventory"
                  values={{ net: formatSigned(netChange) }}
                  components={{ b: <b className="font-semibold text-foreground" /> }}
                />
              </p>
              {recountCount + rejectCount > 0 ? (
                <p>
                  <Trans
                    i18nKey="stocktakes.dialog.approve.leftovers"
                    ns="inventory"
                    values={{ recount: recountCount, reject: rejectCount }}
                    components={{ b: <b className="font-semibold text-foreground" /> }}
                  />
                </p>
              ) : null}
              <p>{t('stocktakes.dialog.approve.next', { org: counterpartyName })}</p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('stocktakes.dialog.cancel')}
          </Button>
          <Button
            size="sm"
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            onClick={onContinue}
          >
            {t('stocktakes.dialog.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Stock drifted between submit and approve
// ---------------------------------------------------------------------------

/** Beyond this the list scrolls AND stops rendering rows: a 2,000 line drift
 *  is a story the first few rows already tell, and mounting all of them makes
 *  the dialog crawl. */
const DRIFT_ROWS_SHOWN = 40;

/**
 * The server refuses an approve whose expected quantities no longer match the
 * shelf (CONFLICT + `STOCKTAKE_DRIFT`), and this is what that refusal looks
 * like. It is a warning, not an error: the reviewer can still go ahead, they
 * just have to see WHAT they are writing over first. Calm copy, no red.
 */
export function StockDriftDialog({
  open,
  onOpenChange,
  locationName,
  lines,
  onApproveAnyway,
  isApproving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationName: string;
  lines: StocktakeDriftedLine[];
  onApproveAnyway: () => void;
  isApproving: boolean;
}) {
  const { t } = useTranslation('inventory');
  const shown = lines.slice(0, DRIFT_ROWS_SHOWN);
  const hidden = lines.length - shown.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('stocktakes.dialog.drift.title')}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2.5 text-left">
              <p>
                {t('stocktakes.dialog.drift.body', {
                  count: lines.length,
                  lines: formatQty(lines.length),
                  location: locationName,
                })}
              </p>
              <p>{t('stocktakes.dialog.drift.consequence')}</p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Guarded: a cause we could not parse a single line out of still gets
            the warning and the choice, never a table of headers and nothing. */}
        {shown.length > 0 ? (
          <div className="max-h-[240px] overflow-y-auto rounded-md border">
            <table className="w-full text-[11.5px]">
              <thead className="sticky top-0 bg-muted/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th scope="col" className="px-2.5 py-1.5 text-left">
                    {t('stocktakes.table.sku')}
                  </th>
                  <th scope="col" className="px-2.5 py-1.5 text-right">
                    {t('stocktakes.dialog.drift.colExpected')}
                  </th>
                  <th scope="col" className="px-2.5 py-1.5 text-right">
                    {t('stocktakes.dialog.drift.colCurrent')}
                  </th>
                  <th scope="col" className="px-2.5 py-1.5 text-right">
                    {t('stocktakes.dialog.drift.colCounted')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {shown.map((line) => (
                  <tr key={line.skuId}>
                    <td className="px-2.5 py-1.5 font-mono">{line.sku}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums text-muted-foreground">
                      {formatQty(line.expectedQty)}
                    </td>
                    <td className="px-2.5 py-1.5 text-right font-semibold tabular-nums">
                      {formatQty(line.currentStock)}
                    </td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums">
                      {formatQty(line.countedQty)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {hidden > 0 ? (
          <p className="text-[11.5px] tabular-nums text-muted-foreground">
            {t('stocktakes.dialog.drift.andMore', { count: hidden })}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('stocktakes.dialog.cancel')}
          </Button>
          <Button
            size="sm"
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            onClick={onApproveAnyway}
            disabled={isApproving}
          >
            {isApproving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {t('stocktakes.dialog.drift.approveAnyway')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Reject the whole count
// ---------------------------------------------------------------------------

export function RejectCountDialog({
  open,
  onOpenChange,
  locationName,
  counterpartyName,
  onContinue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationName: string;
  counterpartyName: string;
  onContinue: () => void;
}) {
  const { t } = useTranslation('inventory');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('stocktakes.dialog.reject.title', { location: locationName })}
          </DialogTitle>
          <DialogDescription className="text-left">
            {t('stocktakes.dialog.reject.body', { org: counterpartyName })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('stocktakes.dialog.cancel')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
            onClick={onContinue}
          >
            {t('stocktakes.dialog.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk approve
// ---------------------------------------------------------------------------

export function BulkApproveDialog({
  open,
  onOpenChange,
  cleanCount,
  flaggedCount,
  onContinue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cleanCount: number;
  flaggedCount: number;
  onContinue: () => void;
}) {
  const { t } = useTranslation('inventory');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('stocktakes.dialog.bulk.title', { count: cleanCount + flaggedCount })}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2.5 text-left">
              <p>
                <Trans
                  i18nKey="stocktakes.dialog.bulk.clean"
                  ns="inventory"
                  values={{ count: cleanCount }}
                  components={{ b: <b className="font-semibold text-foreground" /> }}
                />
              </p>
              {flaggedCount > 0 ? (
                <p>
                  <Trans
                    i18nKey="stocktakes.dialog.bulk.flagged"
                    ns="inventory"
                    values={{ count: flaggedCount }}
                    components={{ b: <b className="font-semibold text-foreground" /> }}
                  />
                </p>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('stocktakes.dialog.cancel')}
          </Button>
          <Button
            size="sm"
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            onClick={onContinue}
          >
            {flaggedCount > 0
              ? t('stocktakes.dialog.bulk.applyThenReview')
              : t('stocktakes.dialog.bulk.applyAll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// New count
// ---------------------------------------------------------------------------

export function NewCountDialog({
  open,
  onOpenChange,
  locationName,
  onDownloadCountSheet,
  onDownloadExample,
  onUploadSheet,
  isPreparingSheet,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationName: string;
  onDownloadCountSheet: () => void;
  onDownloadExample: () => void;
  onUploadSheet: () => void;
  isPreparingSheet: boolean;
}) {
  const { t } = useTranslation('inventory');
  const items = [
    {
      key: 'countSheet',
      // The sheet is built from a fresh product fetch, which is not instant on
      // a big location: the icon slot carries the wait.
      icon: isPreparingSheet ? Loader2 : Download,
      iconClassName: isPreparingSheet ? 'animate-spin' : undefined,
      title: t('stocktakes.dialog.newCount.sheetTitle'),
      description: isPreparingSheet
        ? t('stocktakes.dialog.newCount.sheetPreparing')
        : t('stocktakes.dialog.newCount.sheetDescription'),
      onClick: onDownloadCountSheet,
      disabled: isPreparingSheet,
    },
    {
      key: 'example',
      icon: FileSpreadsheet,
      iconClassName: undefined,
      title: t('stocktakes.dialog.newCount.exampleTitle'),
      description: t('stocktakes.dialog.newCount.exampleDescription'),
      onClick: onDownloadExample,
      disabled: isPreparingSheet,
    },
    {
      key: 'upload',
      icon: Upload,
      iconClassName: undefined,
      title: t('stocktakes.dialog.newCount.uploadTitle'),
      description: t('stocktakes.dialog.newCount.uploadDescription'),
      onClick: onUploadSheet,
      disabled: isPreparingSheet,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('stocktakes.dialog.newCount.title', { location: locationName })}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-1.5">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={item.onClick}
              disabled={item.disabled}
              className="flex items-start gap-3 rounded-md border bg-card px-3 py-2.5 text-left transition-colors duration-200 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
            >
              <item.icon
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground',
                  item.iconClassName,
                )}
                aria-hidden="true"
              />
              <span className="min-w-0">
                <b className="block text-[12.5px] font-semibold">{item.title}</b>
                <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
                  {item.description}
                </span>
              </span>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('stocktakes.dialog.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Notify settings
// ---------------------------------------------------------------------------

export function NotifySettingsDialog({
  open,
  onOpenChange,
  counterpartyName,
  config,
  pingText,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  counterpartyName: string;
  config: StocktakeNotifyConfig;
  pingText: string;
  onSave: (mode: StocktakeNotifyMode, channel: string | null) => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation('inventory');
  const [mode, setMode] = useState<StocktakeNotifyMode>(config.mode);
  const [channel, setChannel] = useState<string | null>(config.channel);

  // Re-seed from the server every time the dialog opens, so a cancelled edit
  // never leaks into the next visit.
  useEffect(() => {
    if (open) {
      setMode(config.mode);
      setChannel(config.channel ?? config.channels[0] ?? null);
    }
  }, [open, config.mode, config.channel, config.channels]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('stocktakes.dialog.notify.title')}</DialogTitle>
          <DialogDescription className="text-left">
            {t('stocktakes.dialog.notify.intro', { org: counterpartyName })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <label
            htmlFor="stocktake-notify-mode"
            className="text-[11.5px] font-semibold text-muted-foreground"
          >
            {t('stocktakes.dialog.notify.label')}
          </label>
          <Select value={mode} onValueChange={(value) => setMode(value as StocktakeNotifyMode)}>
            <SelectTrigger id="stocktake-notify-mode" className="h-8 w-56 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('stocktakes.dialog.notify.optionInApp')}</SelectItem>
              <SelectItem value="slack" disabled={!config.slackConnected}>
                {config.slackConnected
                  ? t('stocktakes.dialog.notify.optionSlack')
                  : t('stocktakes.dialog.notify.optionSlackDisconnected')}
              </SelectItem>
              <SelectItem value="copy">{t('stocktakes.dialog.notify.optionCopy')}</SelectItem>
            </SelectContent>
          </Select>
          {config.slackConnected && config.channels.length > 0 ? (
            <Select value={channel ?? undefined} onValueChange={setChannel}>
              <SelectTrigger
                className="h-8 w-48 text-xs"
                aria-label={t('stocktakes.dialog.notify.channelAria')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.channels.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>

        <div className="text-[11.5px] text-muted-foreground">
          <b className="font-semibold text-foreground">
            {t('stocktakes.dialog.notify.pingPreview')}
          </b>
          <code className="mt-1.5 block whitespace-pre-wrap rounded-md border bg-card px-2.5 py-2 font-mono text-[11px] leading-relaxed text-foreground">
            {pingText}
          </code>
        </div>

        {!config.slackConnected ? (
          <p className="flex items-start gap-2 rounded-md bg-amber-100 px-2.5 py-2 text-[11.5px] text-amber-800 ring-1 ring-amber-300/80">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('stocktakes.dialog.notify.slackWarning')}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('stocktakes.dialog.cancel')}
          </Button>
          <Button
            size="sm"
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            onClick={() => onSave(mode, mode === 'slack' ? channel : null)}
            disabled={isSaving}
          >
            {t('stocktakes.dialog.notify.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Decision-summary composer (post-confirm)
// ---------------------------------------------------------------------------

export function DecisionComposerDialog({
  open,
  onOpenChange,
  title,
  hint,
  cta,
  value,
  onChange,
  onSend,
  pingText,
  notifyLabel,
  onChangeNotify,
  isSending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  hint: string;
  cta: string;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  pingText: string;
  notifyLabel: string | null;
  onChangeNotify?: (() => void) | undefined;
  isSending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {/* `hideTitle`: the DialogHeader above already prints it. */}
        <StocktakeComposer
          title={title}
          hideTitle
          hint={hint}
          cta={cta}
          value={value}
          onChange={onChange}
          onCancel={() => onOpenChange(false)}
          onSend={onSend}
          pingText={pingText}
          notifyLabel={notifyLabel}
          onChangeNotify={onChangeNotify}
          isSending={isSending}
        />
      </DialogContent>
    </Dialog>
  );
}

